import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, PaperPlaneTilt, LockKey } from "@phosphor-icons/react";
import api, { fmtErr } from "../lib/api";

const EMPTY = {
  business_name: "",
  contact_name: "",
  email: "",
  listing_url: "",
  phone: "",
  consent: false,
  website: "",
};

export const LeadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/leads", form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(fmtErr(err));
    }
  };

  return (
    <div
      id="audit-form"
      data-testid="audit-form-card"
      className="card-surface scroll-mt-28 p-8 sm:p-10"
    >
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="flex min-h-[26rem] flex-col items-center justify-center text-center"
            data-testid="audit-form-success"
          >
            <CheckCircle size={64} weight="fill" className="text-pine" />
            <h3 className="mt-6 font-heading text-3xl font-extrabold tracking-tight text-ink">
              Your Audit Is Being Prepared.
            </h3>
            <p className="mt-3 max-w-sm text-base text-soot">
              Thanks, {form.contact_name.split(" ")[0] || "there"}. We prepare
              every audit personally using the listing details you shared.
              We'll email it to{" "}
              <span className="font-semibold text-ink">{form.email}</span> —
              usually within a couple of business days.
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -12 }}
            onSubmit={submit}
            data-testid="audit-form"
          >
            <h3 className="font-heading text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
              Request Your Free Audit
            </h3>
            <p className="mt-2 text-sm text-soot">
              Tell us where to find your listing — we prepare every audit
              personally.
            </p>

            <div className="mt-8 space-y-7">
              <div>
                <label htmlFor="business_name" className="field-label">
                  Business name *
                </label>
                <input
                  id="business_name"
                  name="business_name"
                  data-testid="form-business-name"
                  required
                  value={form.business_name}
                  onChange={update}
                  className="field-input"
                  placeholder="Harbor & Main Plumbing"
                  autoComplete="organization"
                />
              </div>
              <div>
                <label htmlFor="listing_url" className="field-label">
                  Google Maps listing URL *
                </label>
                <input
                  id="listing_url"
                  name="listing_url"
                  data-testid="form-listing-url"
                  required
                  value={form.listing_url}
                  onChange={update}
                  className="field-input"
                  placeholder="https://maps.google.com/…"
                  autoComplete="url"
                />
                <p className="mt-2 text-xs text-soot">
                  Find your business on Google Maps, tap Share, and paste the
                  link here.
                </p>
              </div>
              <div>
                <label htmlFor="contact_name" className="field-label">
                  Your name *
                </label>
                <input
                  id="contact_name"
                  name="contact_name"
                  data-testid="form-contact-name"
                  required
                  value={form.contact_name}
                  onChange={update}
                  className="field-input"
                  placeholder="Jane Miller"
                  autoComplete="name"
                />
              </div>
              <div>
                <label htmlFor="email" className="field-label">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="form-email"
                  required
                  value={form.email}
                  onChange={update}
                  className="field-input"
                  placeholder="jane@yourbusiness.com"
                  autoComplete="email"
                />
              </div>
              <div>
                <label htmlFor="phone" className="field-label">
                  Phone (optional)
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  data-testid="form-phone"
                  value={form.phone}
                  onChange={update}
                  className="field-input"
                  placeholder="Best number to reach you"
                  autoComplete="tel"
                />
              </div>
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={update}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <label
                className="flex cursor-pointer items-start gap-3"
                data-testid="form-consent-label"
              >
                <input
                  type="checkbox"
                  name="consent"
                  required
                  checked={form.consent}
                  onChange={update}
                  data-testid="form-consent-checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[#065F46]"
                />
                <span className="text-xs leading-relaxed text-soot">
                  I agree that LocalLift may review my public business listing
                  and contact me by email about my free audit. *
                </span>
              </label>
            </div>

            {status === "error" && (
              <p
                data-testid="audit-form-error"
                role="alert"
                className="mt-6 rounded-2xl bg-red-50 p-4 text-sm font-medium text-red-700"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="form-submit-button"
              disabled={status === "sending"}
              className="btn-primary mt-9 w-full disabled:opacity-60"
            >
              {status === "sending" ? "Sending…" : "Request My Free Audit"}
              <PaperPlaneTilt weight="bold" size={18} />
            </button>

            <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-soot">
              <LockKey size={16} weight="duotone" className="mt-0.5 shrink-0 text-pine" />
              We only use your details to prepare your audit and contact you
              about it. No spam, no selling your data. See our{" "}
              <a
                href="#privacy"
                data-testid="form-privacy-link"
                className="font-semibold text-pine underline underline-offset-2"
              >
                privacy notice
              </a>
              .
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
